import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useRefLatest } from '../hooks/useRefLatest';
import { useShortUrl } from '../hooks/useShortUrl';
import { ReactComponent as IconCheck } from '../icons/icon-checkmark-inline.svg';
import { ReactComponent as IconCopy } from '../icons/icon-copy.inline.svg';
import { yakliMessages } from '../thread/yakli/yakliMessages';
import { clipboardMessages } from '../utils/clipboard';
import { handleError } from '../utils/common-utils';
import { ButtonV3Icon } from './Button/ButtonV3Icon';
import { Tooltip } from './GraphModal/Tooltip';
import { LoaderBlock } from './LoaderBlock';
import { failureToast, successToast } from './Toast';

interface CopyIconProps {
  messageId: string;
  slug: string;
}

export const CopyIcon: FC<CopyIconProps> = memo(function CopyIcon(props) {
  const { messageId, slug } = props;

  const [{ value, loading, error }, fetch] = useShortUrl({ commentId: messageId, slug });

  const yakliId = useMemo(() => {
    // E.g. 'https://dev.yak.li/7ec4d9'
    return value?.slice(value.lastIndexOf('/') + 1);
  }, [value]);

  const fetchRef = useRefLatest(fetch);
  useEffect(() => {
    // Fetches only at the first render. The reference to the fetch function seems to change each time we call it (bad), we have to deal with it and only use the first reference.
    fetchRef.current();
  }, [fetchRef]);

  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  const [showCopied, setShowCopied] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const valueRef = useRefLatest(value);
  const copy = useCallback(() => {
    if (!valueRef.current) {
      failureToast(<FormattedMessage {...clipboardMessages.noTextToCopy} />, { toastId: 'noText' });
      return;
    }
    navigator.clipboard.writeText(valueRef.current);
    successToast(<FormattedMessage {...yakliMessages.copySuccess} />);

    setShowCopied(true);
    timerRef.current = setTimeout(() => {
      setShowCopied(false);
      timerRef.current = null;
    }, 2000);
  }, [valueRef]);

  return loading ? (
    <LoaderBlock customHeightClass="h-12" />
  ) : (
    <Tooltip content={value}>
      <div
        className="flex-1 px-2 py-1 flex items-center space-x-1 group cursor-pointer"
        onClick={copy}
      >
        <span className="flex-1">{yakliId}</span>
        {showCopied ? (
          <div className="w-10 h-10 flex items-center justify-center text-success">
            <IconCheck className="w-5 h-5" />
          </div>
        ) : (
          <ButtonV3Icon
            variant="text"
            radius="rounded-full"
            noPadding
            icon={<IconCopy className="w-10 h-10 p-2" />}
            className="group-hover:bg-gray-lighter"
          />
        )}
      </div>
    </Tooltip>
  );
});
